// src/services/firebase.ts
//
// Firebase initialization. Pre-launch hardening:
//   - Config is read from env (VITE_FIREBASE_*) instead of being
//     hardcoded. Lets you run staging with different keys without
//     a code change. Falls back to known prod values so a missing
//     .env doesn't blow up boot in CI / local dev for new clones.
//   - Emulator gating reads VITE_USE_EMULATORS at build time
//     instead of a hardcoded `IS_EMULATOR = false` constant.
//   - App Check init logs a warning at console-error level if the
//     site key is missing so the on-call engineer notices when
//     deploys lose abuse protection. (App Check ENFORCEMENT —
//     the server-side knob that actually rejects unauthenticated
//     requests — has to be enabled in the Firebase console; this
//     module can't toggle it.)

import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

// Hardcoded fallbacks. Firebase api keys are not secret — they
// identify the project and are restricted server-side via
// Firestore rules + App Check enforcement. We keep them as a
// fallback so a clone-and-go dev workflow still boots without an
// .env. Production deploys MUST set the VITE_FIREBASE_* env vars
// at build time so staging / prod can diverge.
const FALLBACK_CONFIG = {
  apiKey: "AIzaSyDdKzEJOBVBKT_gg8AVId3I5O_TIur3AIw",
  authDomain: "travelx-46b7a.firebaseapp.com",
  projectId: "travelx-46b7a",
  storageBucket: "travelx-46b7a.firebasestorage.app",
  messagingSenderId: "937859944289",
  appId: "1:937859944289:web:84baa4189b7acdb7de3f20",
  measurementId: "G-3PZ4ERRLD1",
} as const;

const env = (key: string): string | undefined => {
  const v = (import.meta.env as any)[key];
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
};

const firebaseConfig = {
  apiKey: env("VITE_FIREBASE_API_KEY") || FALLBACK_CONFIG.apiKey,
  authDomain: env("VITE_FIREBASE_AUTH_DOMAIN") || FALLBACK_CONFIG.authDomain,
  projectId: env("VITE_FIREBASE_PROJECT_ID") || FALLBACK_CONFIG.projectId,
  storageBucket:
    env("VITE_FIREBASE_STORAGE_BUCKET") || FALLBACK_CONFIG.storageBucket,
  messagingSenderId:
    env("VITE_FIREBASE_MESSAGING_SENDER_ID") ||
    FALLBACK_CONFIG.messagingSenderId,
  appId: env("VITE_FIREBASE_APP_ID") || FALLBACK_CONFIG.appId,
  measurementId:
    env("VITE_FIREBASE_MEASUREMENT_ID") || FALLBACK_CONFIG.measurementId,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, "us-central1");

// ── App Check ──────────────────────────────────────────────────
//
// Two pieces:
//   1. Client init (this file) — installs reCAPTCHA v3 provider.
//   2. SERVER ENFORCEMENT — toggle in Firebase console under
//      Project Settings → App Check, per service. Without #2,
//      this client init does nothing useful; without #1 (or with
//      a missing key), enforcement breaks production.
//
// If you see this warning in prod logs, App Check is not active
// and the apiKey baked into the bundle is your only barrier
// against abuse — fix immediately.
const APP_CHECK_KEY = env("VITE_FIREBASE_APP_CHECK_KEY");
if (APP_CHECK_KEY) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(APP_CHECK_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    // Keep this at error level so it's visible in production logs.
    // eslint-disable-next-line no-console
    console.error("App Check initialization failed:", error);
  }
} else if (import.meta.env.MODE === "production") {
  // eslint-disable-next-line no-console
  console.error(
    "[firebase] VITE_FIREBASE_APP_CHECK_KEY missing — abuse protection is OFF in production. Configure App Check before public launch."
  );
}

// ── Emulators ──────────────────────────────────────────────────
const USE_EMULATORS =
  env("VITE_USE_EMULATORS") === "true" || env("VITE_USE_EMULATORS") === "1";
if (USE_EMULATORS) {
  try {
    connectAuthEmulator(auth, "http://localhost:9099", {
      disableWarnings: true,
    });
    connectFirestoreEmulator(db, "localhost", 8080);
    connectStorageEmulator(storage, "localhost", 9199);
    connectFunctionsEmulator(functions, "localhost", 5001);
    // eslint-disable-next-line no-console
    console.info("[firebase] Connected to local emulators");
  } catch (error) {
    // Already-connected error is benign — happens on hot reload.
    // eslint-disable-next-line no-console
    console.warn("[firebase] Emulator connect skipped:", error);
  }
}

export default app;
